import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { comboboxView } from '../lib/combobox.js';
import { Input } from './ui.js';

/**
 * A small type-to-filter text box with a list of known values under it: a
 * native-feeling combobox (ARIA 1.2 pattern: an input with `role="combobox"`
 * and a `listbox` popup, arrow keys, Enter, Escape) without a dependency.
 *
 * The text stays free: the owner may pick a listed value or keep typing a new
 * one. With `onCreate`, a typed text that no option equals (ignoring case)
 * offers an "add as new" entry, which calls `onCreate` instead of `onChange`.
 * A typed text that differs from an option only in case offers that option,
 * never the add entry, so a picker cannot start a case-duplicate.
 *
 * Keyboard: nothing is highlighted until an arrow key moves into the list, so
 * Enter never swaps the typed text for a partial match. Enter with nothing
 * highlighted picks the option that equals the text ignoring case; failing
 * that, when adding is on, it only highlights the "add" entry (a second Enter,
 * or a click, creates it — a bare Enter never creates, so a typo is not added),
 * and otherwise keeps the text as typed.
 *
 * Pointer: the list sits in the page flow under the input (never on top of the
 * fields or buttons below it, which it pushes down while open), and an option
 * is picked on click. A mousedown anywhere outside hides the list but keeps its
 * space until that click has landed, so the button under the pointer does not
 * move away between mousedown and click; the list then closes.
 *
 * The label is its own `<label for>`, outside the popup, so the listed options
 * never become part of the input's accessible name.
 */
export function Combobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  onCreate,
  createLabel = (text) => `Add “${text}”`,
  disabled = false,
  hint,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The known values, in the order to list them. */
  options: readonly string[];
  placeholder?: string | undefined;
  /** Called with the cleaned text when the owner picks the "add as new" entry. */
  onCreate?: ((text: string) => void) | undefined;
  /** The add entry's text for a typed value. */
  createLabel?: ((text: string) => string) | undefined;
  disabled?: boolean | undefined;
  hint?: ReactNode;
  testId?: string | undefined;
}) {
  const inputId = useId();
  const listId = useId();
  const hintId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Null until the owner types after opening the list: every option shows.
  const [query, setQuery] = useState<string | null>(null);
  // -1: nothing highlighted (the default after typing); an arrow key moves in.
  const [active, setActive] = useState(-1);
  // A mousedown outside hid the list; it closes once the click has landed.
  const [hiding, setHiding] = useState(false);
  const hidingRef = useRef(false);
  const pointerDownRef = useRef(false);

  const view = comboboxView(options, query, onCreate !== undefined);
  const items: { kind: 'option' | 'create'; value: string }[] = [
    ...view.shown.map((value) => ({ kind: 'option' as const, value })),
    ...(view.create !== null ? [{ kind: 'create' as const, value: view.create }] : []),
  ];
  const showList = open && !disabled && (items.length > 0 || view.hidden > 0);
  const activeIndex = active >= items.length ? items.length - 1 : active;

  function close() {
    hidingRef.current = false;
    setHiding(false);
    setOpen(false);
    setQuery(null);
    setActive(-1);
  }

  function choose(item: { kind: 'option' | 'create'; value: string }) {
    if (item.kind === 'create') onCreate?.(item.value);
    else onChange(item.value);
    close();
  }

  // While open, watch the whole document: a mousedown outside hides the list
  // (keeping its space, so nothing under the pointer moves), and the mouseup
  // that ends it closes the list once the click it produces has been handled.
  useEffect(() => {
    if (!open) return undefined;
    const inside = (target: EventTarget | null) =>
      target instanceof Node && rootRef.current !== null && rootRef.current.contains(target);
    const onDown = (event: MouseEvent) => {
      pointerDownRef.current = true;
      if (inside(event.target)) return;
      hidingRef.current = true;
      setHiding(true);
    };
    const onUp = () => {
      pointerDownRef.current = false;
      // After the click event this mouseup produces has run its handlers.
      if (hidingRef.current) window.setTimeout(close, 0);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('mouseup', onUp, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('mouseup', onUp, true);
    };
  }, [open]);

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        setActive((i) => Math.min(i + 1, items.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case 'Enter': {
        if (!showList) break;
        event.preventDefault();
        const highlighted = activeIndex >= 0 ? items[activeIndex] : undefined;
        if (highlighted) choose(highlighted);
        else if (view.exact !== null) choose({ kind: 'option', value: view.exact });
        // Never create on a bare Enter (a typo would become a series): highlight the
        // add entry instead, so a second Enter or a click creates it.
        else if (view.create !== null) setActive(items.length - 1);
        else close(); // free text: keep what was typed
        break;
      }
      case 'Escape':
        if (open) {
          event.preventDefault();
          close();
        }
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  }

  return (
    <div className="block" data-testid={testId} ref={rootRef}>
      <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-body">
        {label}
      </label>
      <Input
        id={inputId}
        role="combobox"
        aria-expanded={showList && !hiding}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          showList && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
        }
        aria-describedby={hint ? hintId : undefined}
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setQuery(e.target.value);
          setActive(-1);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        // A pointer that left the box closes the list through the document
        // listeners (after its click lands); any other blur closes it now.
        onBlur={() => {
          if (!pointerDownRef.current) close();
        }}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          // A name without the field's label, so getByLabel("Series") never finds the list.
          aria-label="Known values"
          aria-hidden={hiding || undefined}
          className={`relative z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-surface py-1 text-sm shadow-lg ring-1 ring-inset ring-line-strong ${
            hiding ? 'invisible' : ''
          }`}
        >
          {items.map((item, i) => (
            <li
              key={`${item.kind}:${item.value}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              // Keep the focus in the input; the pick itself happens on click.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(item)}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3 py-1.5 ${
                i === activeIndex ? 'bg-sunken text-strong' : 'text-body'
              } ${item.kind === 'create' ? 'border-t border-line font-medium text-brand-600' : ''}`}
            >
              {item.kind === 'create' ? createLabel(item.value) : item.value}
            </li>
          ))}
          {view.hidden > 0 && (
            <li role="presentation" className="px-3 py-1.5 text-xs text-muted">
              {view.hidden} more — keep typing to narrow the list
            </li>
          )}
        </ul>
      )}
      {hint && (
        <span id={hintId} className="mt-1 block text-xs text-muted">
          {hint}
        </span>
      )}
    </div>
  );
}
